import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  Policy,
  AttachMoney,
} from '@mui/icons-material';
import { dashboardAPI } from 'services/api';

const AgentAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [policyAnalytics, setPolicyAnalytics] = useState(null);
  const [speciesAnalytics, setSpeciesAnalytics] = useState(null);
  const [geographicAnalytics, setGeographicAnalytics] = useState(null);

  const loadAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all analytics data in parallel
      const [policyResponse, speciesResponse, geographicResponse] = await Promise.all([
        dashboardAPI.getPolicyAnalytics(),
        dashboardAPI.getSpeciesAnalytics(),
        dashboardAPI.getGeographicAnalytics()
      ]);

      setPolicyAnalytics(policyResponse.data);
      setSpeciesAnalytics(speciesResponse.data);
      setGeographicAnalytics(geographicResponse.data);

    } catch (err) {
      console.error('Error loading analytics:', err);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const StatCard = ({ title, value, icon, color = 'primary' }) => (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="textSecondary" gutterBottom variant="h6">
              {title}
            </Typography>
            <Typography variant="h4" component="h2" color={`${color}.main`}>
              {value}
            </Typography>
          </Box>
          <Box color={`${color}.main`}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadAnalytics}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="subtitle1" color="textSecondary" gutterBottom>
        Comprehensive insights into the insurance system performance
      </Typography>

      {/* Policy Analytics */}
      {policyAnalytics && (
        <Box mb={4}>
          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            Policy Analytics
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Policies"
                value={policyAnalytics.total_policies}
                icon={<Policy sx={{ fontSize: 40 }} />}
                color="primary"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Active Policies"
                value={policyAnalytics.active_policies}
                icon={<TrendingUp sx={{ fontSize: 40 }} />}
                color="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Pending Policies"
                value={policyAnalytics.pending_policies}
                icon={<Policy sx={{ fontSize: 40 }} />}
                color="warning"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Plan Distribution"
                value={Object.keys(policyAnalytics.plan_distribution).length}
                icon={<AttachMoney sx={{ fontSize: 40 }} />}
                color="info"
              />
            </Grid>
          </Grid>

          {/* Plan Distribution */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Policy Distribution by Plan
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(policyAnalytics.plan_distribution).map(([plan, count]) => (
                  <Grid item xs={6} sm={4} md={2} key={plan}>
                    <Box textAlign="center" p={2} border={1} borderColor="divider" borderRadius={1}>
                      <Typography variant="h4" color="primary">
                        {count}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Plan {plan}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Species Distribution */}
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Policy Distribution by Species
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(policyAnalytics.species_distribution).map(([species, count]) => (
                  <Grid item xs={6} sm={4} md={2} key={species}>
                    <Box textAlign="center" p={2} border={1} borderColor="divider" borderRadius={1}>
                      <Typography variant="h4" color="secondary">
                        {count}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {species}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Species Analytics */}
      {speciesAnalytics && (
        <Box mb={4}>
          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            Species Analytics
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Species</TableCell>
                  <TableCell align="right">Total Ponds</TableCell>
                  <TableCell align="right">Insured Ponds</TableCell>
                  <TableCell align="right">Insurance Coverage %</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(speciesAnalytics).map(([species, data]) => (
                  <TableRow key={species}>
                    <TableCell>
                      <Chip label={species} color="primary" size="small" />
                    </TableCell>
                    <TableCell align="right">{data.ponds}</TableCell>
                    <TableCell align="right">{data.policies}</TableCell>
                    <TableCell align="right">
                      {data.ponds > 0 ? ((data.policies / data.ponds) * 100).toFixed(2) : 0}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Geographic Analytics */}
      {geographicAnalytics && (
        <Box mb={4}>
          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
            Geographic Distribution
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Location</TableCell>
                  <TableCell align="right">Farms</TableCell>
                  <TableCell align="right">Ponds</TableCell>
                  <TableCell align="right">Policies</TableCell>
                  <TableCell align="right">Avg Policies per Farm</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(geographicAnalytics).map(([location, data]) => (
                  <TableRow key={location}>
                    <TableCell>
                      <Typography variant="body2">{location}</Typography>
                    </TableCell>
                    <TableCell align="right">{data.farms}</TableCell>
                    <TableCell align="right">{data.ponds}</TableCell>
                    <TableCell align="right">{data.policies}</TableCell>
                    <TableCell align="right">
                      {data.farms > 0 ? (data.policies / data.farms).toFixed(2) : 0}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default AgentAnalytics;
